import { calculateGuides } from "./helper";
import { CaptureType } from "../api/cardApiModel";
import { cardScanPackageVersion } from "../version";
import { DeviceInfo, EnvironmentInfo } from "../utils/getInfoFunctions";

export const generateImageFromCanvas = (
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  flipHorizontal: boolean,
  flipVertical: boolean
): string | undefined => {
  const [guideX, guideY, guideWidth, guideHeight] = calculateGuides(
    videoRefBg,
    frameDivRef
  );
  const captureCanvas = captureCanvasRef.current;
  const video = videoRefBg.current;

  if (!captureCanvas || !video) {
    return;
  }

  captureCanvas.width = guideWidth;
  captureCanvas.height = guideHeight;

  //resize image to canvas sizes
  const ctx = captureCanvas?.getContext("2d", { alpha: false });

  ctx?.clearRect(0, 0, captureCanvas.width, captureCanvas.height);

  const scaleX = flipHorizontal ? -1 : 1;
  const scaleY = flipVertical ? -1 : 1;

  const translateX = flipHorizontal ? -captureCanvas.width : 0;
  const translateY = flipVertical ? -captureCanvas.height : 0;

  ctx?.scale(scaleX, scaleY);
  ctx?.translate(translateX, translateY);

  ctx?.drawImage(
    video,
    guideX,
    guideY,
    guideWidth,
    guideHeight,
    0,
    0,
    captureCanvas.width,
    captureCanvas.height
  );

  //The captureCanvas drawImage use a large amount of GPU memory and isn't getting properly garbage collected inside of the loop.
  //To fix this we convert the captured image to a jpeg string, which is stored in RAM.
  //We then clear the canvas context to clear up GPU memory when possible.
  const jpegUrl = captureCanvas.toDataURL("image/jpeg", 0.9);
  ctx?.clearRect(0, 0, captureCanvas.width, captureCanvas.height);
  ctx?.setTransform(1, 0, 0, 1, 0, 0);
  return jpegUrl;
};

export const generateImageMetadata = (
  capture: CaptureType,
  videoRefBg: React.RefObject<HTMLVideoElement>,
  frameDivRef: React.RefObject<HTMLDivElement>,
  captureCanvasRef: React.RefObject<HTMLCanvasElement>,
  threshold: number,
  laplacianThreshold: number,
  videoMetadata: { [key: string]: any },
  environmentInfo: EnvironmentInfo,
  videoElementInfo: { width: number | string; height: number | string },
  guideFrameElementInfo: { width: number | string; height: number | string },
  deviceInfo: DeviceInfo
): { [key: string]: any } => {
  const video = videoRefBg.current;
  const captureCanvas = captureCanvasRef.current;
  const [guideX, guideY, guideWidth, guideHeight] = calculateGuides(
    videoRefBg,
    frameDivRef
  );

  let metadata: { [key: string]: any } = { captureType: capture };

  metadata["guides"] = {
    x: guideX,
    y: guideY,
    width: guideWidth,
    height: guideHeight,
  };
  metadata["captureCanvas"] = {
    width: captureCanvas?.width,
    height: captureCanvas?.height,
  };
  metadata["videoBackground"] = {
    width: video?.videoWidth,
    height: video?.videoHeight,
  };
  metadata["windowInner"] = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  metadata["environmentInfo"] = environmentInfo;
  metadata["deviceInfo"] = deviceInfo;
  metadata["videoElementInfo"] = videoElementInfo;
  metadata["guideFrameElementInfo"] = guideFrameElementInfo;

  if (capture == CaptureType.automatic) {
    metadata["mlThreshold"] = threshold;
    metadata["laplacianThreshold"] = laplacianThreshold;
  }

  metadata["package_name"] = "insurance-cardscan-react";
  metadata["package_version"] = cardScanPackageVersion;

  metadata = Object.assign({}, metadata, videoMetadata);
  return metadata;
};
